import { useMemo } from 'react';
import { gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { fuzzyMatch } from '../../utils/fuzzyMatch';

/**
 * XAF's Find panel (ShowFindPanel="True" on the LLT Search / LLT Depletion
 * views): keeps the rows whose DISPLAYED text in any column matches the search,
 * so a date matches as the grid shows it (MM/DD/YYYY), not as stored.
 *
 * `mode`:
 *  - 'fuzzy' (default) — the app's forgiving match (typos, skipped letters).
 *  - 'contains' — a plain case-insensitive "contains", as G5's find panel and
 *    toolbar Search match (owner request 2026-11-03, LLT Search / Depletion). It is
 *    also what the grid's highlightText marks, so every shown row has a highlight.
 */
export function useGridTextSearch<T>(
  rows: T[], columns: AppColumn<T>[], search: string, mode: 'fuzzy' | 'contains' = 'fuzzy',
): T[] {
  return useMemo(() => {
    const q = search.trim();
    if (!q) return rows;
    const text = gridExportMatrix(rows, columns).rows;
    const needle = q.toLowerCase();
    const hit = mode === 'contains'
      ? (v: string) => v.toLowerCase().includes(needle)
      : (v: string) => fuzzyMatch(q, v);
    return rows.filter((_, i) => text[i]!.some((v) => hit(v ?? '')));
  }, [rows, columns, search, mode]);
}
